import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  GraduationCap,
  BrainCircuit,
  Cloud,
  Code,
  Database,
  Zap,
  Mic,
  Sparkles,
  FlaskConical,
  BarChart3,
  Rocket,
  Activity,
  RotateCcw,
  PenLine,
} from "lucide-react";
import TiltedCard from "./TiltedCard";
import TestimonialsCarousel from "./TestimonialsCarousel";

const skillPills = {
  purple: "bg-purple-500/20 text-purple-100",
  blue: "bg-sky-500/20 text-sky-100",
  green: "bg-emerald-500/20 text-emerald-100",
  orange: "bg-orange-500/20 text-orange-100",
};

const Home = () => {
  const focusCards = [
    {
      icon: <BrainCircuit size={18} />,
      title: "AI & Machine Learning",
      tone: "purple",
      tags: [
        "LLMs",
        "RAG (HNSW, hybrid retrieval)",
        "Agentic AI",
        "LangChain",
        "Fine-tuning (LoRA)",
        "Transformers",
        "Computer Vision",
        "XGBoost",
      ],
    },
    {
      icon: <Cloud size={18} />,
      title: "Cloud Architecture & MLOps",
      tone: "blue",
      tags: [
        "AWS (SageMaker, Lambda, S3)",
        "Azure (Synapse, Data Factory)",
        "Docker",
        "Kubernetes",
        "MLflow",
        "Airflow",
        "GitHub Actions",
        "On-prem HPC (Ollama)",
      ],
    },
    {
      icon: <Code size={18} />,
      title: "Programming & Backend",
      tone: "green",
      tags: [
        "Python",
        "Java (Spring Boot)",
        "TypeScript",
        "Go",
        "FastAPI",
        "React",
        "SQL",
        "REST / gRPC",
      ],
    },
    {
      icon: <Database size={18} />,
      title: "Data Engineering & Analytics",
      tone: "orange",
      tags: [
        "PostgreSQL",
        "Qdrant (vector)",
        "Redis",
        "Kafka",
        "Spark",
        "ETL / Airflow",
        "Power BI (DAX)",
        "Tableau",
      ],
    },
  ];

  const journeySteps = [
    {
      phase: "The Foundation",
      year: "2018 - 2021",
      title: "Senior Systems Engineer at Infosys",
      description:
        "Shipped production code for Apple and Verizon. Built 30+ reusable React components, engineered Spring Boot backend services, and delivered 20+ releases at 99.5% success rate. Won the Infosys MVP Award and Best Team Award.",
      accent: "bg-emerald-500/20 text-emerald-300",
    },
    {
      phase: "The Scale-Up",
      year: "2021 - 2022",
      title: "Software Engineer at Deloitte",
      description:
        "Owned backend architecture serving 10,000+ daily users. Overhauled APIs with Java Spring and Hibernate, pushed system reliability to 99.5% uptime, and built secure deployment pipelines on AWS. Saw the industry shifting toward data, and made the move.",
      accent: "bg-sky-500/20 text-sky-300",
    },
    {
      phase: "The Pivot",
      year: "2022 - 2024",
      title: "M.S. Data Science at GVSU",
      description:
        "Layered R, SAS, and statistical modeling onto a Python and Java foundation. Built MLOps pipelines with scikit-learn and MLflow, designed ETL workflows powering Tableau dashboards for 10+ school districts, and graduated with a 3.9 GPA and the Dean's Scholarship.",
      accent: "bg-purple-500/20 text-purple-300",
    },
    {
      phase: "The Convergence",
      year: "2024 - Present",
      title: "Data Scientist @ GVSU + Corewell Health",
      description:
        "Two AI/ML roles, one thesis. At GVSU: RAG chatbot on Qdrant with sub-50ms retrieval, an XGBoost early-warning model at 92% accuracy, and a real-time Power BI semantic layer on Synapse. Plus $25K+/year saved by hosting Ollama on the on-prem HPC instead of enterprise LLM APIs. At Corewell (part-time): hypothesis-driven analyses (t-tests, ANOVA, Cox proportional hazards) for FDA/IRB-audit-ready research, and an upstream fix to the RISmed R package now used by 200+ researchers. The SDE, DS, and ML lanes I built over 8 years now run in parallel.",
      accent: "bg-orange-500/20 text-orange-300",
    },
  ];

  const shipSteps = [
    {
      icon: <FlaskConical size={18} />,
      title: "Prototype",
      body:
        "Smallest agent that proves the loop. LangChain orchestration, local Ollama for cost-free iteration, structured-output enforcement from day one.",
      tone: "bg-purple-500/20 text-purple-100",
    },
    {
      icon: <BarChart3 size={18} />,
      title: "Eval",
      body:
        "RAGAS metrics (faithfulness, answer relevancy, context precision) on a held-out set. Hallucination rate tracked as a regression test, not a vibe.",
      tone: "bg-sky-500/20 text-sky-100",
    },
    {
      icon: <Rocket size={18} />,
      title: "Ship",
      body:
        "FastAPI on serverless for elastic load. On-prem HPC with 4-bit quantization and batched inference for sensitive data. Sub-second p95 or it does not go live.",
      tone: "bg-emerald-500/20 text-emerald-100",
    },
    {
      icon: <Activity size={18} />,
      title: "Measure",
      body:
        "Structured logging on every prediction: input, output, latency, confidence, retrieval hits. p95/p99 dashboards. Cohort A/B via Microsoft Entra ID when the team is split.",
      tone: "bg-orange-500/20 text-orange-100",
    },
    {
      icon: <RotateCcw size={18} />,
      title: "Iterate",
      body:
        "Swap embedders, tune ef_construction / ef_search, fine-tune with LoRA. Eval gate is binary: the shipped change beats the prior config on the same metrics, or it rolls back.",
      tone: "bg-pink-500/20 text-pink-100",
    },
  ];

  const writingPosts = [
    {
      date: "May 19, 2026",
      title: "Why gRPC Over REST for ML Model Serving",
      hook:
        "Everyone talks about gRPC's performance. The real reason teams switch for ML inference: schema evolution under pressure.",
      url: "https://www.linkedin.com/in/satya-chamana/recent-activity/all/",
      tag: "Production AI",
    },
    {
      date: "May 23, 2026",
      title: "ML Is Not All of AI",
      hook:
        "The engineers who struggle in production ML are not the ones who skipped the math. They are the ones who skipped the mental model underneath it.",
      url: "https://www.linkedin.com/in/satya-chamana/recent-activity/all/",
      tag: "ML Fundamentals",
    },
    {
      date: "May 25, 2026",
      title: "The Tiny Scale Decision Hiding Inside LinkedIn",
      hook:
        "Paste a link in a post, LinkedIn wraps it. Paste it in a comment, raw. Same platform, same link. The best engineering is the work you choose not to do.",
      url: "https://www.linkedin.com/in/satya-chamana/recent-activity/all/",
      tag: "System Design",
    },
  ];

  return (
    <div className="px-[5vw] pt-16 pb-20" style={{ color: "var(--text)" }}>
      <section className="grid items-center gap-12 lg:grid-cols-[1.2fr_0.8fr]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p
            className="text-xs font-semibold uppercase tracking-[0.3em]"
            style={{ color: "var(--text-muted)" }}
          >
            Satya Chamana · AI Engineer · ML · Full-Stack
          </p>
          <h2 className="mt-5 text-4xl font-semibold leading-tight md:text-5xl">
            I didn't pivot to AI.{" "}
            <span style={{ color: "var(--accent)" }}>I grew into it.</span>
          </h2>
          <p
            className="mt-4 max-w-4xl text-lg"
            style={{ color: "var(--text-muted)" }}
          >
            AI/ML Engineer with 8+ years shipping intelligent systems that serve
            10,000+ users at 99.5% uptime. RAG pipelines on Qdrant with sub-50ms
            retrieval, ML models at 92% accuracy, agentic workflows on local
            LLMs. The engineering between the model and the user.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
              style={{
                backgroundColor: "var(--accent)",
                color: "var(--accent-foreground)",
              }}
            >
              See what I've built
              <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold"
              style={{
                borderColor: "var(--border)",
                color: "var(--text-muted)",
              }}
            >
              Let's talk
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4">
            {[
              { label: "Years shipping production", value: "8+" },
              { label: "Uptime @ 10K+ users", value: "99.5%" },
              { label: "ML prediction accuracy", value: "92%" },
              { label: "Saved on LLM infra", value: "$25K+" },
            ].map((metric) => (
              <div key={metric.label}>
                <span className="text-3xl font-semibold">{metric.value}</span>
                <p
                  className="mt-1 text-sm"
                  style={{ color: "var(--text-muted)" }}
                >
                  {metric.label}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="flex justify-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <TiltedCard
            imageSrc="/Chamana-Satya.jpg"
            altText="Satya Chamana"
            containerHeight="420px"
            containerWidth="360px"
            imageHeight="420px"
            imageWidth="360px"
            scaleOnHover={1.03}
            captionText={false}
            showTooltip={false}
          />
        </motion.div>
      </section>

      <section className="mt-24">
        <div className="text-center">
          <button
            className="rounded-full border px-4 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.3em]"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--pill)",
              color: "var(--text-muted)",
            }}
          >
            The Journey
          </button>
          <h2 className="mt-4 text-3xl font-semibold md:text-4xl">
            Every step was building toward{" "}
            <span style={{ color: "var(--accent)" }}>this moment</span>
          </h2>
          <p
            className="mt-3 max-w-2xl mx-auto"
            style={{ color: "var(--text-muted)" }}
          >
            From writing my first Spring Boot service to building RAG pipelines
            and leading data teams. This wasn't a career change. It was a career
            compounding.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {journeySteps.map((step, index) => (
            <motion.article
              key={step.phase}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative rounded-3xl border p-6"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`rounded-full px-3 py-1 text-[0.7rem] font-semibold ${step.accent}`}
                >
                  {step.phase}
                </span>
                <span
                  className="text-xs"
                  style={{ color: "var(--text-muted)" }}
                >
                  {step.year}
                </span>
              </div>
              <h3 className="mt-3 text-lg font-semibold">{step.title}</h3>
              <p
                className="mt-2 text-sm"
                style={{ color: "var(--text-muted)" }}
              >
                {step.description}
              </p>
            </motion.article>
          ))}
        </div>

        <div
          className="mt-8 rounded-3xl border p-6 text-center"
          style={{
            borderColor: "var(--border)",
            background: `linear-gradient(to right, var(--accent-glow), transparent)`,
          }}
        >
          <div className="flex items-center justify-center gap-2">
            <Zap size={18} style={{ color: "var(--accent)" }} />
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              <span className="font-semibold" style={{ color: "var(--text)" }}>
                What's next?
              </span>{" "}
              A Senior ML/AI Engineer seat where these eight years compound, building production AI systems that ship to real users.
            </p>
          </div>
          <div className="mt-3 flex items-center justify-center gap-2">
            <Sparkles size={16} style={{ color: "var(--accent)" }} />
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              <span className="font-semibold" style={{ color: "var(--text)" }}>
                Currently:
              </span>{" "}
              FinSight Phase 1 (Go + gRPC + portfolio risk). Karpathy's
              zero-to-hero series. Tracking the open-source LLM landscape
              weekly.
            </p>
          </div>
        </div>
      </section>

      <section
        className="mt-20 rounded-[32px] border px-6 py-10 md:px-10"
        style={{
          backgroundColor: "var(--bg-soft)",
          borderColor: "var(--border)",
        }}
      >
        <div className="mx-auto max-w-2xl text-center">
          <button
            className="rounded-full border px-4 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.3em]"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--pill)",
              color: "var(--text-muted)",
            }}
          >
            What I Bring
          </button>
          <h2 className="mt-4 text-3xl font-semibold md:text-4xl">
            Code. Data. Intelligence. All in one engineer.
          </h2>
          <p className="mt-3" style={{ color: "var(--text-muted)" }}>
            Most people specialize in one lane. I built the road across all of
            them.
          </p>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <article
            className="flex flex-col gap-4 rounded-3xl border p-6"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "var(--border)",
            }}
          >
            <div className="flex items-center gap-3">
              <span
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl"
                style={{ backgroundColor: "var(--pill)" }}
              >
                <GraduationCap size={18} />
              </span>
              <h3 className="text-lg font-semibold">Background</h3>
            </div>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Built backend systems with Java Spring Boot and React for 3 years,
              shipping 20+ production releases for clients like Apple and
              Verizon. Scaled APIs to 99.5% uptime for 10,000+ daily users at
              Deloitte, then earned a Master's in Data Science (3.9 GPA),
              layering R, SAS, and statistical modeling onto a strong Python and
              Java foundation. Now building RAG pipelines, XGBoost classifiers,
              ETL workflows, and Power BI dashboards while leading a data team
              of 4.
            </p>
          </article>

          {focusCards.map((card) => (
            <article
              key={card.title}
              className="flex flex-col gap-4 rounded-3xl border p-6"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${skillPills[card.tone]}`}
                >
                  {card.icon}
                </span>
                <h3 className="text-lg font-semibold">{card.title}</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {card.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`rounded-full px-3 py-1 text-[0.7rem] font-semibold ${skillPills[card.tone]}`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-8 flex justify-center">
          <Link
            to="/about"
            className="inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold"
            style={{
              backgroundColor: "var(--accent)",
              color: "var(--accent-foreground)",
            }}
          >
            Full Background
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      <section className="mt-20">
        <div className="text-center">
          <button
            className="rounded-full border px-4 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.3em]"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--pill)",
              color: "var(--text-muted)",
            }}
          >
            How I Ship
          </button>
          <h2 className="mt-4 text-3xl font-semibold md:text-4xl">
            The loop between{" "}
            <span style={{ color: "var(--accent)" }}>model and user</span>
          </h2>
          <p
            className="mt-3 max-w-2xl mx-auto"
            style={{ color: "var(--text-muted)" }}
          >
            My specialty is putting a model in front of real users. Here is the
            production loop I run.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {shipSteps.map((step, index) => (
            <motion.article
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className="rounded-3xl border p-5"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${step.tone}`}
                >
                  {step.icon}
                </span>
                <span
                  className="text-[0.6rem] font-semibold uppercase tracking-[0.25em]"
                  style={{ color: "var(--text-muted)" }}
                >
                  0{index + 1}
                </span>
              </div>
              <h3 className="mt-4 text-base font-semibold">{step.title}</h3>
              <p
                className="mt-2 text-sm leading-relaxed"
                style={{ color: "var(--text-muted)" }}
              >
                {step.body}
              </p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="mt-20">
        <div className="text-center">
          <button
            className="rounded-full border px-4 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.3em]"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--pill)",
              color: "var(--text-muted)",
            }}
          >
            Recent
          </button>
          <h2 className="mt-4 text-3xl font-semibold md:text-4xl">
            What's been{" "}
            <span style={{ color: "var(--accent)" }}>compounding lately</span>
          </h2>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl border p-6"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "var(--border)",
            }}
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-purple-500/20 text-purple-100">
                <Mic size={18} />
              </span>
              <div>
                <span
                  className="block text-[0.65rem] font-semibold uppercase tracking-[0.3em]"
                  style={{ color: "var(--text-muted)" }}
                >
                  Speaker · Aug 2025
                </span>
                <h3 className="text-lg font-semibold">
                  AI Awareness Lunch & Learn
                </h3>
              </div>
            </div>
            <p
              className="mt-4 text-sm leading-relaxed"
              style={{ color: "var(--text-muted)" }}
            >
              Delivered a hands-on session on LLMs, Agentic AI, RAG, and MCP to
              60+ healthcare professionals at Kirkhof College of Nursing. 95%
              positive feedback.
            </p>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-3xl border p-6"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "var(--border)",
            }}
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-500/20 text-sky-100">
                <Sparkles size={18} />
              </span>
              <div>
                <span
                  className="block text-[0.65rem] font-semibold uppercase tracking-[0.3em]"
                  style={{ color: "var(--text-muted)" }}
                >
                  Invited · June 2026
                </span>
                <h3 className="text-lg font-semibold">
                  Vector Space Day SF · Qdrant
                </h3>
              </div>
            </div>
            <p
              className="mt-4 text-sm leading-relaxed"
              style={{ color: "var(--text-muted)" }}
            >
              Attending on personal invite from Andre Zayarni (Qdrant CEO).
              Joining the Bay Area vector-search community in person, and
              taking the production-AI conversation off LinkedIn and into the
              room.
            </p>
          </motion.article>
        </div>
      </section>

      <section className="mt-20">
        <div className="text-center">
          <button
            className="rounded-full border px-4 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.3em]"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--pill)",
              color: "var(--text-muted)",
            }}
          >
            Writing
          </button>
          <h2 className="mt-4 text-3xl font-semibold md:text-4xl">
            Notes from the{" "}
            <span style={{ color: "var(--accent)" }}>production AI seam</span>
          </h2>
          <p
            className="mt-3 max-w-2xl mx-auto"
            style={{ color: "var(--text-muted)" }}
          >
            Where the model ends and the engineering begins. Three recent
            pieces.
          </p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {writingPosts.map((post, index) => (
            <motion.a
              key={post.title}
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className="group flex flex-col rounded-3xl border p-6 transition-shadow hover:shadow-[0_20px_45px_rgba(0,0,0,0.35)]"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
            >
              <div className="flex items-center justify-between">
                <span
                  className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.2em]"
                  style={{
                    backgroundColor: "var(--pill)",
                    color: "var(--text-muted)",
                  }}
                >
                  <PenLine size={12} />
                  {post.tag}
                </span>
                <span
                  className="text-xs"
                  style={{ color: "var(--text-muted)" }}
                >
                  {post.date}
                </span>
              </div>
              <h3 className="mt-4 text-lg font-semibold leading-snug">
                {post.title}
              </h3>
              <p
                className="mt-3 text-sm leading-relaxed flex-1"
                style={{ color: "var(--text-muted)" }}
              >
                {post.hook}
              </p>
              <span
                className="mt-4 inline-flex items-center gap-1 text-xs font-semibold"
                style={{ color: "var(--accent)" }}
              >
                Read on LinkedIn
                <ArrowUpRight size={14} />
              </span>
            </motion.a>
          ))}
        </div>
      </section>

      <TestimonialsCarousel />

      <section className="mt-16 text-center">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Shipped production systems at
        </p>
        <div
          className="mt-5 flex flex-wrap justify-center gap-6 text-sm"
          style={{ color: "var(--text-muted)" }}
        >
          {[
            "Infosys (Apple, Verizon)",
            "Deloitte",
            "Corewell Health",
            "Grand Valley State University",
          ].map((client) => (
            <span key={client}>{client}</span>
          ))}
        </div>
      </section>

      {/* <section className="mt-20">
        <div className="text-center">
          <button
            className="rounded-full border px-4 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.3em]"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--pill)",
              color: "var(--text-muted)",
            }}
          >
            Beyond the Code
          </button>
          <h2 className="mt-4 text-3xl font-semibold md:text-4xl">
            Through My Lens
          </h2>
          <p className="mt-3" style={{ color: "var(--text-muted)" }}>
            When I'm not building systems, I'm out capturing moments.
            Photography is how I slow down and see the world differently.
          </p>
        </div>
        <div className="mt-10">
          <Masonry
            items={[
              { id: 1, img: "p1.jpg", height: 700 },
              { id: 2, img: "p2.jpg", height: 700 },
              { id: 3, img: "p3.jpg", height: 500 },
              { id: 4, img: "p4.jpg", height: 650 },
              { id: 5, img: "p5.jpg", height: 400 },
              { id: 6, img: "p6.jpg", height: 550 },
              { id: 7, img: "p7.jpg", height: 480 },
              { id: 8, img: "p8.jpg", height: 520 },
            ]}
            ease="power3.out"
            duration={0.6}
            stagger={0.05}
            animateFrom="bottom"
            scaleOnHover={true}
            hoverScale={0.95}
            blurToFocus={true}
          />
        </div>
      </section> */}
    </div>
  );
};

export default Home;
